import './LocationLoadOptionManager.scss';

export interface LocationLoadOptionManagerProps {
  /** The org's active fulfilment methods, already sorted for display. */
  options: any[];
  /** Method ids explicitly assigned to this delivery location. */
  explicitlyEnabledIds: Set<string>;
  /** Whether the location has any methods of its own (vs inheriting the org default). */
  hasLocationSpecificLoadOptions: boolean;
  /** The default method for this location (address default, else org default). */
  defaultFulfillmentMethodId: string | null;
  onEnable: (loadOptionId: string) => void | Promise<void>;
  onDisable: (loadOptionId: string) => void | Promise<void>;
  /** An enable/disable write is in flight. */
  pending?: boolean;
}

/**
 * The "Manage methods" panel: a checkbox list of the org's fulfilment methods,
 * toggling each on/off for one customer delivery location. Writes are immediate
 * (the host mutation persists straight away and affects future orders). The
 * inherited organization default is shown checked and locked, because a location
 * with no methods of its own always falls back to it.
 */
export function LocationLoadOptionManager({
  options,
  explicitlyEnabledIds,
  hasLocationSpecificLoadOptions,
  defaultFulfillmentMethodId,
  onEnable,
  onDisable,
  pending = false,
}: LocationLoadOptionManagerProps) {
  return (
    <div className="location-load-option-manager">
      <div className="location-load-option-manager__title">
        Methods available for this customer location
      </div>

      <div className="muted tiny location-load-option-manager__hint">
        Changes here update the customer location immediately and affect future orders.
      </div>

      {options.length === 0 ? (
        <div className="muted tiny">
          No active fulfilment methods are available for this organization.
        </div>
      ) : (
        <div className="location-load-option-manager__rows">
          {options.map((option: any) => {
            const optionId = option.id;
            const isExplicitlyEnabled = explicitlyEnabledIds.has(optionId);
            const isInheritedOrganizationDefault =
              !hasLocationSpecificLoadOptions &&
              Boolean(option.isDefault) &&
              optionId === defaultFulfillmentMethodId;

            const checked = isExplicitlyEnabled || isInheritedOrganizationDefault;

            return (
              <label
                key={optionId}
                className="location-load-option-manager__row"
                style={{ cursor: isInheritedOrganizationDefault ? 'default' : 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={pending || isInheritedOrganizationDefault}
                  onChange={async (event) => {
                    if (event.target.checked) {
                      await onEnable(optionId);
                    } else {
                      await onDisable(optionId);
                    }
                  }}
                />

                <span>
                  {option.name}
                  {option.capacityLabel ? ` (${option.capacityLabel}t)` : ''}
                  {isInheritedOrganizationDefault
                    ? ' • Organization default'
                    : isExplicitlyEnabled && optionId === defaultFulfillmentMethodId
                      ? ' • Default'
                      : ''}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LocationLoadOptionManager;
